/**
 * performanceTracker.ts — TheBurntPeanut Simulator
 *
 * Tracks per-frame timing + entity load so bug reports automatically include
 * FPS, lag spikes, and what was happening on screen when each drop occurred.
 *
 * Usage:
 *   resetPerformanceTracker()          — call at session start
 *   recordFrame(dt, context)           — call every game loop frame
 *   getPerformanceSummary()            — call when building a bug report
 *   getCurrentFps()                    — live FPS for optional HUD display
 */

const DROP_FPS_THRESHOLD = 30;   // < 30fps counts as a drop
const RING_SIZE          = 180;  // ~3s of history at 60fps
const MAX_DROPS          = 30;   // cap the drop log

// ── Types ─────────────────────────────────────────────────────────────────────

export interface FrameContext {
  enemies?:     number;
  projectiles?: number;
  particles?:   number;
  lootOrbs?:    number;
  phase?:       string;   // 'playing' | 'levelup' | 'chest' | 'paused' | …
}

interface FpsDrop {
  atSeconds: number;
  fps:       number;
  frameMs:   number;      // raw frame duration so the exact number is there
  context:   FrameContext;
}

// ── Singleton state ───────────────────────────────────────────────────────────

const recentFrameMs: number[] = [];  // ring buffer of recent frame durations
const dropLog: FpsDrop[]      = [];

let sessionStart = 0;
let totalFrames  = 0;
let sumFrameMs   = 0;
let worstFrameMs = 0;  // highest ms = worst fps moment

// Track peak entity counts seen during the session
let peakEnemies     = 0;
let peakProjectiles = 0;
let peakParticles   = 0;
let peakLootOrbs    = 0;

// Off-screen kill tracking
let totalKills      = 0;
let offScreenKills  = 0;

// ── Public API ────────────────────────────────────────────────────────────────

/** Call once at the start of each run. Resets everything. */
export function resetPerformanceTracker(): void {
  recentFrameMs.length = 0;
  dropLog.length       = 0;
  sessionStart         = Date.now();
  totalFrames          = 0;
  sumFrameMs           = 0;
  worstFrameMs         = 0;
  peakEnemies          = 0;
  peakProjectiles      = 0;
  peakParticles        = 0;
  peakLootOrbs         = 0;
  totalKills           = 0;
  offScreenKills       = 0;
}

/**
 * Record an enemy kill and whether it happened inside the visible viewport.
 * Call this from the weapon adapter whenever an enemy's HP hits 0.
 *
 * @param enemyX     World X of the killed enemy
 * @param enemyY     World Y of the killed enemy
 * @param playerX    Player world X (camera center)
 * @param playerY    Player world Y (camera center)
 * @param viewHalfW  Half the viewport width in world units  (canvasW / 2 / zoom)
 * @param viewHalfH  Half the viewport height in world units (canvasH / 2 / zoom)
 */
export function recordKill(
  enemyX: number, enemyY: number,
  playerX: number, playerY: number,
  viewHalfW: number, viewHalfH: number,
): void {
  totalKills++;
  const dx = Math.abs(enemyX - playerX);
  const dy = Math.abs(enemyY - playerY);
  if (dx > viewHalfW || dy > viewHalfH) offScreenKills++;
}

/**
 * Call every game loop frame.
 * @param dt       Delta-time in seconds (already capped by game loop).
 * @param context  Optional snapshot of entity counts and current phase.
 * @param realDt   BETA 19.75 — uncapped real delta-time (before the 100ms
 *                 clamp). Used ONLY for worstFrameMs tracking so we can see
 *                 the actual spike magnitude (PSBOT cycle-30az ask). The
 *                 clamped `dt` is still used for averaging + dt-based logic
 *                 to keep game simulation stable.
 */
export function recordFrame(dt: number, context?: FrameContext, realDt?: number): void {
  if (dt <= 0) return;
  const ms = dt * 1000;
  // Use the un-clamped real frame time for worst-spike telemetry (PSBOT ask),
  // fall back to ms if not provided so legacy callsites still log something.
  // BETA 20.77 — audit fix: realDt had NO upper guard, so any ≥1s rAF gap
  // (tab defocus, window drag, OS stall — Dylan's run had 368s of menus)
  // recorded as a "1049ms frame". Mirror the GameCanvas guard (realDt < 1).
  const realMs = (realDt !== undefined && realDt > 0 && realDt < 1) ? realDt * 1000 : ms;

  totalFrames++;
  sumFrameMs += ms;
  if (realMs > worstFrameMs) worstFrameMs = realMs;

  // Rolling window
  recentFrameMs.push(ms);
  if (recentFrameMs.length > RING_SIZE) recentFrameMs.shift();

  // Track peak entity counts
  if (context) {
    if ((context.enemies     ?? 0) > peakEnemies)     peakEnemies     = context.enemies!;
    if ((context.projectiles ?? 0) > peakProjectiles) peakProjectiles = context.projectiles!;
    if ((context.particles   ?? 0) > peakParticles)   peakParticles   = context.particles!;
    if ((context.lootOrbs    ?? 0) > peakLootOrbs)    peakLootOrbs    = context.lootOrbs!;
  }

  // Log drops below threshold, debounced to 1 per second max
  const fps = 1000 / ms;
  if (fps < DROP_FPS_THRESHOLD) {
    const atSeconds = Math.round((Date.now() - sessionStart) / 1000);
    const lastDrop  = dropLog[dropLog.length - 1];
    if ((!lastDrop || atSeconds - lastDrop.atSeconds >= 1) && dropLog.length < MAX_DROPS) {
      dropLog.push({
        atSeconds,
        fps:     Math.round(fps),
        frameMs: Math.round(ms),
        context: { ...context },
      });
    }
  }
}

/**
 * Returns a multi-line performance summary for bug reports.
 * Includes session averages, worst frames, peak entity counts, and drop details.
 */
export function getPerformanceSummary(): string {
  if (totalFrames < 10) return '[PERF] not enough data (run too short)';

  const avgFps    = Math.round(1000 / (sumFrameMs / totalFrames));
  const minFps    = worstFrameMs > 0 ? Math.round(1000 / worstFrameMs) : avgFps;
  const recentAvg = recentFrameMs.length > 0
    ? Math.round(1000 / (recentFrameMs.reduce((a, b) => a + b, 0) / recentFrameMs.length))
    : avgFps;

  const killLine = totalKills > 0
    ? `[KILLS] total:${totalKills}  off-screen:${offScreenKills} (${Math.round(offScreenKills / totalKills * 100)}%)  — enemies dying outside visible viewport`
    : '[KILLS] no kills recorded';

  const lines: string[] = [
    `[PERF] avg:${avgFps}fps  recent:${recentAvg}fps  worst:${minFps}fps (${Math.round(worstFrameMs)}ms)  drops:${dropLog.length}`,
    `[PERF] peak entities — enemies:${peakEnemies}  projs:${peakProjectiles}  particles:${peakParticles}  orbs:${peakLootOrbs}`,
    killLine,
  ];

  if (dropLog.length > 0) {
    lines.push('[PERF] drop log (time@fps | entities at drop):');
    const shown = dropLog.slice(-10);  // last 10 drops
    for (const d of shown) {
      const ctx   = d.context;
      const ctxStr = [
        ctx.enemies     != null ? `en:${ctx.enemies}`   : null,
        ctx.projectiles != null ? `pr:${ctx.projectiles}` : null,
        ctx.particles   != null ? `pt:${ctx.particles}` : null,
        ctx.lootOrbs    != null ? `lo:${ctx.lootOrbs}`  : null,
        ctx.phase       != null ? ctx.phase              : null,
      ].filter(Boolean).join(' ');
      lines.push(`  ${d.atSeconds}s @ ${d.fps}fps (${d.frameMs}ms)  [${ctxStr || 'no ctx'}]`);
    }
  }

  return lines.join('\n');
}

/** Returns current FPS based on the last 10 frames — good for a live HUD readout. */
export function getCurrentFps(): number {
  if (recentFrameMs.length === 0) return 60;
  const slice = recentFrameMs.slice(-10);
  const avg   = slice.reduce((a, b) => a + b, 0) / slice.length;
  return Math.round(1000 / avg);
}

/**
 * BETA 16.71 — Per-run perf snapshot for live_play telemetry.
 * Returns avg / worst / dropped-frame count so PSBOT can spot
 * platform-specific perf patterns (e.g. iPhone runs averaging 28fps,
 * desktop averaging 60fps, ROG Ally 45fps).
 */
export interface PerfRunSnapshot {
  avgFps: number;
  worstFps: number;
  worstFrameMs: number;
  droppedFrames: number;
  totalFrames: number;
}
export function getPerfRunSnapshot(): PerfRunSnapshot {
  const avgFps = totalFrames > 0
    ? Math.round(1000 / (sumFrameMs / totalFrames))
    : 0;
  const worstFps = worstFrameMs > 0 ? Math.round(1000 / worstFrameMs) : avgFps;
  return {
    avgFps,
    worstFps,
    worstFrameMs: Math.round(worstFrameMs),
    droppedFrames: dropLog.length,
    totalFrames,
  };
}

// ─── BETA 13.70 FPS: per-system frame timing ──────────────────────────────────
// Lets the dev panel + CSV pinpoint which system caused a drop.
// Bracket hot systems with notePerf('name', performance.now() - t0).
const _systemTimes: Record<string, number[]> = {};
const _SYSTEM_TIMES_CAP = 180; // ~3s of 60fps history per system

/** Per-frame system timing (avg/worst over a rolling 180-frame window) for the dev panel.
 *  autoPlaytester.notePerf already buckets *cumulative* ms for the CSV; this captures the
 *  per-frame distribution so the dev panel can show "avg ms / worst ms" per system. */
export function notePerfFrame(system: string, ms: number): void {
  let arr = _systemTimes[system];
  if (!arr) { arr = []; _systemTimes[system] = arr; }
  arr.push(ms);
  if (arr.length > _SYSTEM_TIMES_CAP) arr.shift();
}

export function getPerfBreakdown(): Record<string, { avg: number; worst: number }> {
  const result: Record<string, { avg: number; worst: number }> = {};
  for (const sys in _systemTimes) {
    const times = _systemTimes[sys];
    if (times.length === 0) continue;
    let sum = 0;
    let worst = 0;
    for (const t of times) {
      sum += t;
      if (t > worst) worst = t;
    }
    result[sys] = {
      avg: Math.round((sum / times.length) * 100) / 100,
      worst: Math.round(worst * 100) / 100,
    };
  }
  return result;
}

export function resetPerfBreakdown(): void {
  for (const k in _systemTimes) delete _systemTimes[k];
}
